import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { BellRing, Mail, Smartphone, Inbox, Save, RefreshCw, Layers, Eye, MessageSquare } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const CHANNELS = [
  { key: "email", label: "Email", icon: Mail },
  { key: "inapp", label: "In-app", icon: Inbox },
  { key: "push", label: "Push", icon: Smartphone },
  { key: "sms", label: "SMS", icon: MessageSquare },
];

const CATEGORY_LABELS = {
  paie: "Paie",
  documents: "Documents",
  planning: "Planning",
  conges: "Congés & absences",
  support: "Support",
  facturation: "Facturation",
  education: "Éducation",
  reservation: "Réservations & clients",
  rh: "RH — contrats",
  commercial: "Commercial — CRM",
  systeme: "Système",
};

/** Réglages de notification par entreprise (Phase 38). Utilisé côté chef. */
export const NotificationSettingsPanel = () => {
  const [catalog, setCatalog] = useState([]);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/chef/notification-settings");
      setCatalog(data.catalog || []);
      setValues(data.events || {});
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const toggle = (eventKey, channel) => {
    setValues((v) => ({
      ...v,
      [eventKey]: { ...(v[eventKey] || {}), [channel]: !(v[eventKey] || {})[channel] },
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { events: {} };
      catalog.forEach((e) => {
        const conf = values[e.key] || {};
        payload.events[e.key] = {
          email: conf.email !== false,
          inapp: conf.inapp !== false,
          push: conf.push !== false,
          sms: conf.sms !== false,
        };
      });
      const { data } = await api.put("/chef/notification-settings", payload);
      setValues(data.events || {});
      toast.success("Préférences de notification enregistrées");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const groups = catalog.reduce((acc, e) => {
    (acc[e.category] = acc[e.category] || []).push(e);
    return acc;
  }, {});

  return (
    <div className="border border-[#DEE2E6] bg-white p-5" data-testid="notification-settings-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="font-display font-bold text-lg flex items-center gap-2">
            <BellRing className="h-5 w-5 text-[#002FA7]" />Notifications de l'entreprise
          </div>
          <div className="text-[11px] text-[#495057]">
            Choisissez, par événement, les canaux utilisés pour votre équipe. Un canal désactivé n'envoie plus rien
            (ni email, ni cloche, ni push, ni SMS) pour cet événement. Les SMS ne concernent que les alertes
            importantes et partent vers le téléphone renseigné sur la fiche de chacun.
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} data-testid="notif-settings-reload">
            <RefreshCw className="h-4 w-4 mr-1" />Recharger
          </Button>
          <Button onClick={save} disabled={saving || loading} className="bg-[#002FA7] text-white" data-testid="notif-settings-save">
            <Save className="h-4 w-4 mr-1" />{saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-xs text-[#868E96]">Chargement…</div>
      ) : (
        <div className="space-y-4">
          <DigestPanel />
          {Object.entries(groups).map(([cat, list]) => (
            <div key={cat} className="border border-[#DEE2E6]" data-testid={`notif-group-${cat}`}>
              <div className="px-3 py-2 bg-[#F8F9FA] text-[10px] uppercase tracking-[0.2em] font-bold border-b border-[#DEE2E6]">
                {CATEGORY_LABELS[cat] || cat}
              </div>
              <div className="divide-y divide-[#DEE2E6]">
                {list.map((e) => (
                  <div key={e.key} className="px-3 py-2.5 flex flex-wrap items-center gap-2">
                    <div className="flex-1 min-w-[180px]">
                      <div className="text-sm font-semibold">{e.label}</div>
                      <div className="text-[10px] text-[#868E96]">Destinataire : {e.audience}</div>
                    </div>
                    <div className="flex gap-1.5">
                      {CHANNELS.filter((c) => e.channels.includes(c.key)).map((c) => {
                        const on = (values[e.key] || {})[c.key] !== false;
                        return (
                          <button
                            key={c.key}
                            type="button"
                            onClick={() => toggle(e.key, c.key)}
                            data-testid={`notif-toggle-${e.key}-${c.key}`}
                            className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-1 border transition ${
                              on ? "bg-[#059669] text-white border-[#059669]" : "bg-white text-[#868E96] border-[#DEE2E6]"
                            }`}
                          >
                            <c.icon className="h-3 w-3" />{c.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/** Digest quotidien (Phase 43) — un seul email récapitulatif au lieu de N envois. */
export const DigestPanel = () => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/chef/notification-digest");
      setCfg(data);
    } catch (e) { /* silencieux */ }
  };
  useEffect(() => { load(); }, []);

  const [preview, setPreview] = useState(null);

  const save = async (patch) => {
    const next = { ...cfg, ...patch };
    setCfg(next);
    setSaving(true);
    try {
      const { data } = await api.put("/chef/notification-digest", {
        enabled: !!next.enabled,
        hour: parseInt(next.hour) || 18,
        max_priority: next.max_priority || "medium",
        categories: next.categories || [],
        timezone: next.timezone || "Europe/Paris",
      });
      setCfg(data);
      toast.success(data.enabled ? "Digest quotidien enregistré" : "Digest quotidien désactivé");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const toggleCat = (c) => { const cur = cfg.categories || []; save({ categories: cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c] }); };
  const openPreview = async () => {
    try { const { data } = await api.get("/chef/notification-digest/preview"); setPreview(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!cfg) return null;

  return (
    <div className="border-2 border-[#002FA7] bg-[#F5F8FF] p-4" data-testid="digest-panel">
      <div className="flex flex-wrap items-start gap-3">
        <Layers className="h-5 w-5 text-[#002FA7] mt-0.5" />
        <div className="flex-1 min-w-[220px]">
          <div className="font-display font-bold text-sm">Digest quotidien</div>
          <div className="text-[11px] text-[#495057]">
            Regroupe les notifications de faible importance en <b>un seul email récapitulatif</b> par jour.
            Les alertes urgentes (absence, impayé) continuent de partir immédiatement.
          </div>
        </div>
        <button type="button" onClick={() => save({ enabled: !cfg.enabled })} disabled={saving}
                data-testid="digest-toggle"
                className={`text-[10px] uppercase tracking-wider font-bold px-3 py-2 border transition ${
                  cfg.enabled ? "bg-[#059669] text-white border-[#059669]" : "bg-white text-[#868E96] border-[#DEE2E6]"
                }`}>
          {cfg.enabled ? "Activé" : "Désactivé"}
        </button>
      </div>

      {cfg.enabled && (
        <div className="mt-3 flex flex-wrap items-end gap-4">
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Heure d'envoi (heure locale)</Label>
            <div className="flex items-center gap-1 mt-1">
              <Input type="number" min="0" max="23" value={cfg.hour} data-testid="digest-hour"
                     className="h-9 w-20" onChange={(e) => setCfg({ ...cfg, hour: e.target.value })}
                     onBlur={() => save({ hour: cfg.hour })} />
              <span className="text-[11px] text-[#868E96]">h</span>
              <select value={cfg.timezone || "Europe/Paris"} onChange={(e) => save({ timezone: e.target.value })} data-testid="digest-timezone"
                      className="h-9 border border-[#DEE2E6] bg-white px-2 text-xs ml-1">
                {(cfg.timezones || ["Europe/Paris"]).map((tz) => <option key={tz} value={tz}>{tz.replace("_", " ")}</option>)}
              </select>
            </div>
            <div className="text-[10px] text-[#868E96] mt-1">Il est actuellement {cfg.local_hour} h dans ce fuseau.</div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Regrouper jusqu'à la priorité</Label>
            <div className="flex gap-1 mt-1">
              {[{ k: "low", l: "Basse" }, { k: "medium", l: "Moyenne" }, { k: "high", l: "Haute" }].map((p) => (
                <button key={p.k} type="button" onClick={() => save({ max_priority: p.k })}
                        data-testid={`digest-prio-${p.k}`}
                        className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1.5 border ${
                          cfg.max_priority === p.k ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-[#868E96] border-[#DEE2E6]"
                        }`}>
                  {p.l}
                </button>
              ))}
            </div>
          </div>
          <div className="text-[11px] text-[#495057] pb-2" data-testid="digest-pending">
            En attente de regroupement : <b>{cfg.pending}</b>
          </div>
          <Button size="sm" variant="outline" onClick={openPreview} data-testid="digest-preview" className="h-9 mb-0.5"><Eye className="h-3.5 w-3.5 mr-1" />Aperçu de l'email</Button>
          <div className="w-full">
            <Label className="text-[10px] uppercase tracking-wider">Catégories regroupées <span className="normal-case font-normal text-[#868E96]">(aucune cochée = toutes)</span></Label>
            <div className="flex flex-wrap gap-1 mt-1" data-testid="digest-categories">
              {(cfg.categories_catalog || []).map((c) => {
                const on = (cfg.categories || []).includes(c);
                return <button key={c} type="button" onClick={() => toggleCat(c)} data-testid={`digest-cat-${c}`} data-on={on ? "1" : "0"}
                               className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1.5 border ${on ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-[#868E96] border-[#DEE2E6]"}`}>{CATEGORY_LABELS[c] || c}</button>;
              })}
            </div>
          </div>
        </div>
      )}
      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) setPreview(null); }}>
        <DialogContent className="max-w-2xl" data-testid="digest-preview-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl font-black">{preview?.subject}</DialogTitle><DialogDescription className="sr-only">Aperçu de l'email récapitulatif</DialogDescription></DialogHeader>
          {preview && <>
            <div className="text-[11px] text-[#868E96]" data-testid="digest-preview-meta">{preview.sample ? "Exemple (aucune notification en attente pour l'instant)" : `${preview.count} notification(s) en attente seront regroupées`}</div>
            <iframe title="Aperçu digest" srcDoc={preview.html} className="w-full h-[60vh] border border-[#DEE2E6] bg-white" data-testid="digest-preview-frame" />
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NotificationSettingsPanel;
